import { getScrollBehavior } from 'core/utils/motion';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import type { SelectCheckboxOption } from './SelectCheckboxs';

type PickyRenderItemProps = {
  index: number;
  item: SelectCheckboxOption;
  isSelected: boolean;
  selectValue: (item: SelectCheckboxOption) => void;
  labelKey?: string;
  valueKey?: string;
  isMultiple?: boolean;
  disabled?: boolean;
};

type PickyRenderSelectAllProps = {
  filtered: boolean;
  tabIndex: number;
  allSelected: 'all' | 'none' | 'partial';
  toggleSelectAll: () => void;
  multiple: boolean;
};

export type { PickyRenderItemProps, PickyRenderSelectAllProps };

type Params = {
  disabled: boolean;
  isError?: boolean;
  label?: React.ReactNode;
  labelKey: string;
  onChange: (value: SelectCheckboxOption[]) => void;
  options: SelectCheckboxOption[];
  shouldDebounce: boolean;
  value: SelectCheckboxOption[];
  valueKey: string;
};

function useSelectCheckboxs({
  disabled,
  isError,
  label,
  labelKey,
  onChange,
  options,
  shouldDebounce,
  value,
  valueKey,
}: Params) {
  const { t } = useTranslation(['common', 'placeholder']);

  const [valueState, setValueState] = useState<SelectCheckboxOption[]>(value);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const onChangeRef = useRef(onChange);
  const uniqueIdRef = useRef(`select-checkboxs-${uuidv4()}`);

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    setValueState(value);
  }, [value]);

  useEffect(() => {
    if (!containerRef.current) return;
    const trigger = containerRef.current.querySelector('.picky__input');
    if (trigger) {
      trigger.setAttribute('aria-invalid', String(Boolean(isError)));
    }
  }, [isError, value, options, disabled]);

  useEffect(() => {
    if (!containerRef.current) return;
    const combobox = containerRef.current.querySelector<HTMLElement>('[role="combobox"]');
    if (!combobox) return;

    function applyAriaFixes(): void {
      if (!combobox) return;
      const expected = String(isDropdownOpen);
      if (combobox.getAttribute('aria-expanded') !== expected) {
        combobox.setAttribute('aria-expanded', expected);
      }
      if (combobox.hasAttribute('aria-controls')) {
        combobox.removeAttribute('aria-controls');
      }
      if (combobox.getAttribute('aria-owns')?.trim() === '') {
        combobox.removeAttribute('aria-owns');
      }
    }

    applyAriaFixes();

    const observer = new MutationObserver(applyAriaFixes);
    observer.observe(combobox, {
      attributes: true,
      attributeFilter: ['aria-expanded', 'aria-controls', 'aria-owns'],
    });

    return () => observer.disconnect();
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!containerRef.current) return;
    const combobox = containerRef.current.querySelector<HTMLElement>('[role="combobox"]');
    if (!combobox) return;

    if (labelRef.current) {
      const comboboxId = combobox.id || uniqueIdRef.current;
      labelRef.current.id = `${comboboxId}-label`;
      combobox.setAttribute('aria-labelledby', `${comboboxId}-label`);
    } else {
      combobox.removeAttribute('aria-labelledby');
    }
  }, [label]);

  useEffect(() => {
    if (!isDropdownOpen) {
      setFocusedOptionIndex(-1);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (focusedOptionIndex >= 0 && optionRefs.current[focusedOptionIndex]) {
      optionRefs.current[focusedOptionIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: getScrollBehavior(),
      });
    }
  }, [focusedOptionIndex]);

  const debounceCheck = useCallback(
    debounce((newList: SelectCheckboxOption[]) => {
      onChangeRef.current(newList);
    }, 100),
    []
  );

  useEffect(() => {
    return () => {
      debounceCheck.cancel();
    };
  }, [debounceCheck]);

  useEffect(() => {
    if (shouldDebounce) {
      debounceCheck(valueState);
    } else {
      onChangeRef.current(valueState);
    }
  }, [valueState, shouldDebounce, debounceCheck]);

  const optionIndexMap = useMemo(() => {
    const map = new Map<unknown, number>();
    options.forEach((opt, i) => {
      map.set(opt[valueKey], i);
    });
    return map;
  }, [options, valueKey]);

  function handleClose(): void {
    setIsDropdownOpen(false);
  }

  function handleOpen(): void {
    setIsDropdownOpen(true);
    const firstEnabledIndex = options.findIndex((opt) => !opt.disabled);
    setFocusedOptionIndex(firstEnabledIndex);
  }

  function onChangeState(data: SelectCheckboxOption | SelectCheckboxOption[]): void {
    const dataArray = Array.isArray(data) ? data : [data];
    const disabledOptions = options.filter((opt) => opt.disabled);
    const currentDisabledSelected = disabledOptions.filter(
      (disabledOpt) =>
        Array.isArray(valueState) &&
        valueState.some((selectedItem) => selectedItem[valueKey] === disabledOpt[valueKey])
    );
    const newDataWithoutDisabled = dataArray.filter((item) => {
      const optionItem = options.find((opt) => opt[valueKey] === item[valueKey]);
      return !optionItem?.disabled;
    });
    setValueState([...currentDisabledSelected, ...newDataWithoutDisabled]);
  }

  function handleOptionKeyDown(
    event: React.KeyboardEvent,
    item: SelectCheckboxOption,
    selectValue: (item: SelectCheckboxOption) => void
  ): void {
    const key = event.key;

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      if (!item.disabled) {
        selectValue(item);
      }
      return;
    }

    if (key === 'ArrowDown') {
      event.preventDefault();
      let nextIndex = focusedOptionIndex + 1;
      while (nextIndex < options.length && options[nextIndex]?.disabled) {
        nextIndex++;
      }
      if (nextIndex < options.length) {
        setFocusedOptionIndex(nextIndex);
      }
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      let prevIndex = focusedOptionIndex - 1;
      while (prevIndex >= 0 && options[prevIndex]?.disabled) {
        prevIndex--;
      }
      if (prevIndex >= 0) {
        setFocusedOptionIndex(prevIndex);
      }
    }
  }

  const hasValue = Array.isArray(valueState) && valueState.length > 0;
  const allItemsSelected = hasValue && options.length === valueState.length;

  return {
    containerRef,
    focusedOptionIndex,
    handleClose,
    handleOpen,
    handleOptionKeyDown,
    hasValue,
    allItemsSelected,
    isDropdownOpen,
    labelKey,
    labelRef,
    onChangeState,
    optionIndexMap,
    optionRefs,
    setFocusedOptionIndex,
    t,
    valueKey,
    valueState,
  };
}

export default useSelectCheckboxs;
